import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import ReusableTable from "components/Tables/ReusableTable";
import { Flex, Box, Text, Badge } from "@chakra-ui/react";
import { FaCheckCircle } from "react-icons/fa";
import { EllipsisVertical, Eye, Archive, ShieldAlert } from "lucide-react";
import ThreeDotMenu from "components/Menus/ThreeDotMenu";
import ViewMessage from "./components/ViewMessage";
import ReusableDrawer from "components/Drawers/ReusableDrawer";
import ArchiveMessage from "./components/ArchiveMessage";
import { fetchContactSupportMessagesAction } from "state/actions/contactSupportActions";
import { formatDate } from "utils/dateFormatter";

const MessagesTab = () => {
  const dispatch = useDispatch();
  const { messages, loading, error } = useSelector((state) => state.supportMessages);

  const [selectedMessage, setSelectedMessage] = useState(null);
  const [isViewMessageDrawerOpen, setViewMessageDrawerOpen] = useState(false);
  const [isArchiveMessageDialogOpen, setArchiveMessageDialogOpen] = useState(false);
  const [selectedMessageForArchive, setSelectedMessageForArchive] = useState(null);

  useEffect(() => {
    dispatch(fetchContactSupportMessagesAction());
  }, [dispatch]);

  const handleOpenArchiveMessageDialog = (message) => {
    setSelectedMessageForArchive(message);
    setArchiveMessageDialogOpen(true);
  };

  const handleCloseArchiveMessageDialog = () => {
    setSelectedMessageForArchive(null);
    setArchiveMessageDialogOpen(false);
  };

  const handleArchiveMessage = () => {
    console.log("Archiving message:", selectedMessageForArchive);
  };

  const openViewMessageDrawer = (message) => {
    setSelectedMessage(message);
    setViewMessageDrawerOpen(true);
  };

  const closeViewMessageDrawer = () => {
    setSelectedMessage(null);
    setViewMessageDrawerOpen(false);
  };

  const statusColorScheme = {
    Unread: "purple",
    Resolved: "green",
    Pending: "yellow",
  };

  const actionColorScheme = {
    Resolved: { color: "green", icon: <FaCheckCircle /> },
    Pending: { color: "gray.500", icon: null },
  };

  const generateMenuItems = (row) => {


    return [
      {
        icon: <Eye size={16} />,
        text: "View Message",
        onClick: () => openViewMessageDrawer(row.original),
      },
      {
        icon: <Archive size={16} />,
        text: "Archive Message",
        onClick: () => handleOpenArchiveMessageDialog(row.original),
        extraIcon: <ShieldAlert size={16} color="pink" />,
      },
    ];
  };


  const columnsData = [
    {
      Header: "Support Type",
      accessor: "message.support_type",
    },
    // {
    //   Header: "Package Number",
    //   accessor: "message.package_number",
    // },
    {
      Header: "Message",
      accessor: "message.message",
      Cell: ({ value }) => (
        <Text isTruncated maxWidth="200px">
          {value}
        </Text>
      ),
    },
    {
      Header: "User",
      accessor: "user",
      Cell: ({ value }) => (
        <Text>
          {value?.first_name && value?.last_name
            ? `${value.first_name} ${value.last_name}`
            : value?.username || "Unknown"}
        </Text>
      ),
    },
    {
      Header: "Phone Number",
      accessor: "user.phone_number",
      Cell: ({ value }) => <Text>{value || "N/A"}</Text>,
    },
    {
      Header: "Date Added",
      accessor: "message.created_at",
      Cell: ({ value }) => formatDate(value),
    },
    {
      Header: "Status",
      accessor: "message.status",
      Cell: ({ value }) => (
        <Badge colorScheme={statusColorScheme[value]} borderRadius="full" px={2}>
          {value}
        </Badge>
      ),
    },
    {
      Header: "Actions",
      accessor: "moreOptions",
      Cell: ({ row }) => (
        <ThreeDotMenu
          icon={<EllipsisVertical size={20} color="grey" />}
          items={generateMenuItems(row)}
        />
      ),
    },
  ];



  return (
    <Flex flexDirection="column">
      <Box>
        {loading ? (
          <Skeleton width="200px" height="20px" />
        ) : (
          <Text fontSize="md" fontWeight="bold">
            All Messages: {messages.length}
          </Text>
        )}
      </Box>

      <ReusableTable columnsData={columnsData} tableData={messages} isLoading={loading} />

      {/* ReusableDrawer for viewing message details */}
      <ReusableDrawer
        isOpen={isViewMessageDrawerOpen}
        onClose={closeViewMessageDrawer}
        headerContent={<h2 style={{ fontSize: "md" }}>View Message</h2>}
        bodyContent={
          selectedMessage && (
            <ViewMessage message={selectedMessage} onClose={closeViewMessageDrawer} />
          )
        }
      />

      {/* Archive Message */}
      <ArchiveMessage
        isOpen={isArchiveMessageDialogOpen}
        onClose={handleCloseArchiveMessageDialog}
        onConfirm={handleArchiveMessage}
      />
    </Flex>
  );
};

export default MessagesTab;
